import { GIANT_REST, type GiantBone } from '../../models/giant.ts';
import { BonePose, clip, type Bones, type Clip } from '../clip.ts';
import { TAU, envelope, inCubic, inExpo, inOut, inQuad, outBack, outCubic, outExpo, outQuad, phase, settle, smooth, span, type Ease } from '../curves.ts';
import { apply, timeline, type Beat, type KeyPose } from '../pose.ts';

/*
 * El Gigante dormido. Two clip sets on one skeleton: asleep (it sleepwalks: slow, swaying, head lolling from
 * shoulder to shoulder, arms floating before it with the hands hanging) and awake (upright, panting, fists
 * clenched, a fast lumbering stride). Each model's rest pose is already its stance (models/giant.ts), so every
 * pose here is written absolute (as rotations from a figure standing straight with its limbs hanging) and
 * turned into offsets from that variant's rest. The wake clip goes from the sleeper's stance to the awake one,
 * so it plays right on either model.
 *
 * Conventions as in human.ts: -X on an arm or leg swings it forward; +X on a shin bends the knee back, -X on a
 * forearm bends the elbow; +X on a trunk or head leans it forward; +X on the jaw opens it; +X on a hand curls it
 * back (down, when the arm is raised); the figure faces +Z with its `R` limbs on +X.
 *
 * Blows: attack lands at 0.13 s (it starts cocked), throw lets go at 0.6 s, stomp hits the ground at 0.5 s and
 * the wake roar opens at 1.6 s. Die is long (2.9 s): it buckles, drops to its knees and pitches onto its face,
 * and the watchtower on its shoulder tumbles off.
 */

type GB = 'root' | GiantBone;
type P = KeyPose<GB>;
type Pose = (b: Bones<GB>, t: number) => void;
type Rest = Partial<Record<GiantBone, readonly [number, number, number]>>;

/** A variant's stance as an absolute pose (its rest). */
const stanceOf = (r: Rest): P => {
  const o: P = {};
  for (const n of Object.keys(r) as GiantBone[]) {
    const v = r[n] as readonly [number, number, number];
    o[n] = [v[0], v[1], v[2]];
  }
  return o;
};

const S_ST = stanceOf(GIANT_REST.sleep);
const A_ST = stanceOf(GIANT_REST.awake);

/** A pose sheet that makes any bone it is asked for (for sampling a pose at another time). */
const scratch = (): Bones<GB> => {
  const o: Record<string, BonePose> = {};
  return new Proxy(o, { get: (_, k: string) => (o[k] ??= new BonePose()) }) as Bones<GB>;
};

interface Follow {
  bone: GB;
  chain: readonly GB[];
  lag: number;
  k: number;
  hang?: number;
}

/** Secondary motion (as in bosses.ts): each follower swings against how its chain moved a beat earlier. */
const follow = (pose: Pose, dur: number, loop: boolean, fs: readonly Follow[]): Pose => (b, t) => {
  pose(b, t);
  const wrap = (x: number): number => (loop ? ((x % dur) + dur) % dur : Math.max(0, x));
  const cache = new Map<number, Bones<GB>>();
  const at = (x: number): Bones<GB> => {
    const w = wrap(x);
    let s = cache.get(w);
    if (!s) {
      s = scratch();
      pose(s, w);
      cache.set(w, s);
    }
    return s;
  };
  for (const f of fs) {
    const s1 = at(t - f.lag);
    const s2 = at(t - 2 * f.lag);
    const sum = (bb: Bones<GB>, ax: 'rx' | 'ry' | 'rz'): number => f.chain.reduce((a, n) => a + bb[n][ax], 0);
    const bp = b[f.bone];
    for (const ax of ['rx', 'ry', 'rz'] as const) {
      const c0 = sum(b, ax), c1 = sum(s1, ax), c2 = sum(s2, ax);
      const hang = ax === 'ry' ? 0 : (f.hang ?? 0);
      bp[ax] += -hang * c0 - f.k * (c0 - c1) + 0.55 * f.k * (c1 - c2);
    }
  }
};

/** The tower nods late, the cloak, the loincloth and the wrist chain hang plumb and swing, the hands dangle. */
const FOLLOW: readonly Follow[] = [
  { bone: 'tower', chain: ['root', 'body', 'chest'], lag: 0.09, k: 0.9 },
  { bone: 'cloak', chain: ['root', 'body', 'chest'], lag: 0.11, k: 1.1, hang: 0.75 },
  { bone: 'rag', chain: ['root', 'body'], lag: 0.09, k: 1.2, hang: 1 },
  { bone: 'chain', chain: ['root', 'body', 'chest', 'armL', 'foreL'], lag: 0.1, k: 1.3, hang: 1 },
  { bone: 'handL', chain: ['root', 'body', 'chest', 'armL', 'foreL'], lag: 0.08, k: 0.7 },
  { bone: 'handR', chain: ['root', 'body', 'chest', 'armR', 'foreR'], lag: 0.08, k: 0.7 },
];

const kick = (t: number, t0: number, freq = 2.5, damp = 7): number => (t < t0 ? 0 : settle(t - t0, freq, damp));
const shiver = (t: number, f: number): number => 0.6 * Math.sin(TAU * f * t) + 0.4 * Math.sin(TAU * f * 1.7 * t + 1.3);

/** Whole-figure squash from impacts. */
const thud = (t: number, hits: readonly [number, number][]): [number, number, number] => {
  let k = 0;
  for (const [t0, a] of hits) k += a * Math.max(0, kick(t, t0, 3, 9));
  return [1 + 0.6 * k, 1 - k, 1 + 0.6 * k];
};

/** Everything a variant's clips need: its stance, and absolute poses turned into offsets from its rest. */
interface Kit {
  awake: boolean;
  ST: P;
  /** Absolute pose → offsets from this variant's rest. */
  rel(p: P): P;
  /** Plays absolute beats over the stance (a beat names only what changes from it). */
  tl(b: Bones<GB>, t: number, beats: readonly (readonly [number, P, Ease?])[], lag?: Partial<Record<GB, number>>): void;
  /** Sets the bones to an absolute pose (the stance by default). */
  base(b: Bones<GB>, p?: P): void;
}

const kitOf = (awake: boolean): Kit => {
  const R: Rest = GIANT_REST[awake ? 'awake' : 'sleep'];
  const ST = awake ? A_ST : S_ST;
  const rel = (p: P): P => {
    const o: P = { ...p };
    for (const n of Object.keys(R) as GiantBone[]) {
      const r = R[n] as readonly [number, number, number];
      const c = p[n];
      o[n] = [(c?.[0] ?? 0) - r[0], (c?.[1] ?? 0) - r[1], (c?.[2] ?? 0) - r[2], c?.[3] ?? 0, c?.[4] ?? 0, c?.[5] ?? 0, c?.[6] ?? 1];
    }
    return o;
  };
  return {
    awake,
    ST,
    rel,
    tl: (b, t, beats, lag) => timeline(b, t, beats.map(([at, p, e]) => (e ? [at, rel({ ...ST, ...p }), e] : [at, rel({ ...ST, ...p })]) as Beat<GB>), lag),
    base: (b, p = ST) => apply(b, rel(p)),
  };
};

/** The boulder is only in its hand while it throws. */
const noRock = (b: Bones<GB>): void => {
  b.rock.scale(0.0001);
};

const gclip = (name: string, dur: number, loop: boolean, pose: Pose, squash?: (t: number) => [number, number, number], label?: string, rock = false): Clip<GB> =>
  clip<GB>(name, dur, loop, follow((b, t) => {
    pose(b, t);
    if (!rock) noRock(b);
  }, dur, loop, FOLLOW), squash, label);

/* ---------- loops ---------- */

/** Asleep: slow breaths, a sway, the head rolling from one shoulder to the other, the arms floating as if on water. */
const idleSleep = (k: Kit): Clip<GB> =>
  gclip('idle', 6, true, (b, t) => {
    const p = phase(t, 6) * TAU;
    const br = Math.sin(p * 2);
    k.base(b);
    // The roll: lingers on each shoulder and hangs forward as it passes the middle.
    const u = smooth((1 - Math.cos(p)) / 2);
    b.head.rot(0.22 * Math.sin(p) ** 2 + 0.03 * br, 0.3 * u, -0.7 * u);
    b.jaw.rot(0.05 * (1 + Math.sin(p * 2 + 0.4)));
    b.chest.rot(-0.03 * br, 0.04 * Math.sin(p), 0.03 * Math.sin(p + 0.5));
    b.body.rot(0.012 * br, 0, 0.015 * Math.sin(p));
    b.root.rot(0, 0, 0.02 * Math.sin(p)).pos(0, -0.012 * (1 + br), 0);
    b.armL.rot(0.06 * Math.sin(p + 0.4) + 0.03 * br, 0.04 * Math.sin(p), 0.03 * Math.sin(p * 2));
    b.armR.rot(0.06 * Math.sin(p + 2.2) + 0.03 * br, -0.04 * Math.sin(p + 1), -0.03 * Math.sin(p * 2 + 1));
    b.foreL.rot(-0.06 * Math.sin(p + 1.4));
    b.foreR.rot(-0.06 * Math.sin(p + 3.0));
    b.handL.rot(0.12 * Math.sin(p * 2 + 1.2));
    b.handR.rot(0.12 * Math.sin(p * 2 + 2.6));
    b.legL.rot(0, 0, -0.02 * Math.sin(p));
    b.legR.rot(0, 0, -0.02 * Math.sin(p));
  }, (t) => {
    const br = Math.sin(phase(t, 6) * TAU * 2);
    return [1 + 0.008 * br, 1 - 0.006 * br, 1 + 0.008 * br];
  });

/** Awake: heavy panting, fists working, a glare that sweeps the field. */
const idleAwake = (k: Kit): Clip<GB> =>
  gclip('idle', 3, true, (b, t) => {
    const p = phase(t, 3) * TAU;
    const br = Math.sin(p * 3);
    k.base(b);
    b.chest.rot(-0.045 * br, 0.06 * Math.sin(p), 0);
    b.body.rot(0.015 * br, 0, 0.02 * Math.sin(p));
    b.root.pos(0, -0.015 * (1 + br), 0);
    b.head.rot(0.04 * br, 0.35 * Math.sign(Math.sin(p)) * Math.abs(Math.sin(p)) ** 0.5, 0.04 * Math.sin(p));
    b.jaw.rot(0.12 * (1 + Math.sin(p * 3 + 0.6)));
    b.armL.rot(0.04 * br, 0, -0.05 * br);
    b.armR.rot(0.04 * br, 0, 0.05 * br);
    b.foreL.rot(-0.12 * Math.max(0, Math.sin(p + 1)) ** 2);
    b.foreR.rot(-0.12 * Math.max(0, Math.sin(p + 2.5)) ** 2);
    b.handL.rot(0.15 * Math.max(0, Math.sin(p * 2)));
    b.handR.rot(0.15 * Math.max(0, Math.sin(p * 2 + 2)));
  }, (t) => {
    const br = Math.sin(phase(t, 3) * TAU * 3);
    return [1 + 0.01 * br, 1 - 0.008 * br, 1 + 0.01 * br];
  });

/** A footfall jolt: an impulse every half cycle, at the quarter. */
const joltAt = (t: number, dur: number, freq: number, damp: number): number => {
  const half = dur / 2;
  return settle((((t - dur / 4) % half) + half) % half, freq, damp);
};

/** The sleepwalk: slow, shuffling, swaying from foot to foot, arms afloat, the head lolling on every step. */
const walkSleep = (k: Kit, dur = 2.8): Clip<GB> =>
  gclip('walk', dur, true, (b, t) => {
    const th = phase(t, dur) * TAU;
    const sn = Math.sin(th), cs = Math.cos(th);
    const jolt = joltAt(t, dur, 2.2, 6);
    k.base(b);
    const A = 0.27;
    b.legL.rot(-A * sn - 0.02);
    b.legR.rot(A * sn - 0.02);
    b.shinL.rot(0.5 * Math.max(0, cs) ** 1.5);
    b.shinR.rot(0.5 * Math.max(0, -cs) ** 1.5);
    // The weight rolls over the planted foot.
    b.root.rot(0, 0, -0.04 * cs).pos(-0.03 * cs, 0.02 * Math.cos(2 * th) - 0.02 - 0.025 * Math.max(0, jolt), 0);
    b.body.rot(0.03 * jolt, 0.06 * sn, 0.02 * cs);
    b.chest.rot(0.02 * Math.cos(2 * th - 0.6), -0.1 * sn, 0.03 * cs);
    b.head.rot(0.08 * jolt + 0.04 * Math.cos(2 * th - 1), 0.08 * sn, 0.1 * cs - 0.04 * jolt);
    b.jaw.rot(0.05 * Math.max(0, jolt));
    b.armL.rot(0.1 * jolt + 0.06 * sn, 0.05 * sn, 0);
    b.armR.rot(0.1 * jolt - 0.06 * sn, 0.05 * sn, 0);
    b.foreL.rot(-0.05 * cs);
    b.foreR.rot(0.05 * cs);
  }, (t) => {
    const j = Math.max(0, joltAt(t, dur, 2.2, 9));
    return [1 + 0.02 * j, 1 - 0.03 * j, 1 + 0.02 * j];
  });

/** Awake: a lumbering charge, twice as fast; arms pumping, fists down, every footfall a jolt. */
const walkAwake = (k: Kit, dur = 1.5): Clip<GB> =>
  gclip('walk', dur, true, (b, t) => {
    const th = phase(t, dur) * TAU;
    const sn = Math.sin(th), cs = Math.cos(th);
    const jolt = joltAt(t, dur, 2.6, 6);
    k.base(b);
    const A = 0.42;
    b.legL.rot(-A * sn - 0.05);
    b.legR.rot(A * sn - 0.05);
    b.shinL.rot(0.9 * Math.max(0, cs) ** 1.5 + 0.05);
    b.shinR.rot(0.9 * Math.max(0, -cs) ** 1.5 + 0.05);
    b.root.rot(0, 0, -0.035 * cs).pos(-0.02 * cs, 0.035 * Math.cos(2 * th) - 0.04 - 0.035 * Math.max(0, jolt), 0);
    b.body.rot(0.1 + 0.04 * jolt, 0.08 * sn, 0.02 * cs);
    b.chest.rot(0.06 + 0.03 * Math.cos(2 * th - 0.6), -0.16 * sn, 0.03 * cs);
    b.head.rot(-0.12 - 0.05 * jolt, 0.08 * sn, -0.03 * cs);
    b.jaw.rot(0.06 * (1 + Math.cos(2 * th)));
    b.armL.rot(0.38 * Math.sin(th - 0.4), 0, -0.05);
    b.armR.rot(-0.38 * Math.sin(th - 0.4), 0, 0.05);
    b.foreL.rot(-0.25 * Math.max(0, -Math.sin(th - 0.8)) + 0.06 * jolt);
    b.foreR.rot(-0.25 * Math.max(0, Math.sin(th - 0.8)) + 0.06 * jolt);
  }, (t) => {
    const j = Math.max(0, joltAt(t, dur, 2.6, 9));
    return [1 + 0.025 * j, 1 - 0.035 * j, 1 + 0.025 * j];
  });

/* ---------- blows ---------- */

/** A backhand of the right arm (lands at 0.13 s): it starts wound across its chest and sweeps out wide. */
const attack = (k: Kit): Clip<GB> =>
  gclip('attack', 1.3, false, (b, t) => {
    k.tl(b, t, [
      [0, { body: [0.1, -0.15, 0], chest: [0.12, -0.6, 0.05], armR: [-1.3, 0, -1.0], foreR: [-1.15, 0, 0], handR: [0.3, 0, 0], armL: [-0.55, 0, -0.35], foreL: [-0.5, 0, 0], legR: [-0.1, 0, 0.06], shinR: [0.25, 0, 0], legL: [0.05, 0, -0.06], shinL: [0.15, 0, 0], root: [0, 0, 0, 0, -0.03, 0] }],
      [0.05, { body: [0.12, -0.2, 0], chest: [0.14, -0.7, 0.05], armR: [-1.35, 0, -1.08], foreR: [-1.2, 0, 0], handR: [0.35, 0, 0], armL: [-0.55, 0, -0.35], foreL: [-0.5, 0, 0], legR: [-0.1, 0, 0.06], shinR: [0.25, 0, 0], legL: [0.05, 0, -0.06], shinL: [0.15, 0, 0], root: [0, 0, 0, 0, -0.035, 0] }, outQuad],
      [0.13, { body: [0.22, 0.2, 0], chest: [0.24, 0.55, -0.06], armR: [-1.4, 0, 0.75], foreR: [-0.15, 0, 0], handR: [-0.15, 0, 0], armL: [-0.3, 0, -0.5], foreL: [-0.35, 0, 0], legL: [-0.32, 0, -0.06], shinL: [0.38, 0, 0], legR: [0.12, 0, 0.06], shinR: [0.2, 0, 0], root: [0, 0, 0, 0, -0.07, 0.07] }, inCubic],
      [0.42, { body: [0.25, 0.28, 0], chest: [0.28, 0.72, -0.08], armR: [-1.2, 0, 1.05], foreR: [-0.25, 0, 0], handR: [0.2, 0, 0], armL: [-0.28, 0, -0.5], foreL: [-0.35, 0, 0], legL: [-0.3, 0, -0.06], shinL: [0.36, 0, 0], legR: [0.1, 0, 0.06], shinR: [0.2, 0, 0], root: [0, 0, 0, 0, -0.065, 0.065] }, outCubic],
      [1.3, {}, inOut],
    ], { foreR: 0.02, handR: 0.04, head: 0.05 });
    const hit = kick(t, 0.13, 3, 7);
    b.head.rot(0.05 * hit, -0.3 * span(t, 0.05, 0.2) * (1 - span(t, 0.5, 1.2)), 0);
    b.root.pos(0.01 * kick(t, 0.13, 7, 9), 0, 0);
  }, (t) => thud(t, [[0.13, 0.025]]));

const hit = (k: Kit): Clip<GB> =>
  gclip('hit', 0.45, false, (b, t) => {
    k.base(b);
    const q = settle(t, 2.2, 8) * span(t, 0, 0.03, outQuad);
    b.chest.rot(-0.1 * q, 0.06 * q);
    b.body.rot(-0.04 * q);
    b.head.rot(-0.22 * q, -0.1 * q, 0.06 * q);
    b.root.pos(0, 0, -0.03 * q);
    b.armL.rot(0.12 * q, 0, -0.06 * q);
    b.armR.rot(0.12 * q, 0, 0.06 * q);
    if (!k.awake) b.jaw.rot(0.15 * q);
  });

/** Stunned: knees gone soft, arms fallen, the great head circling. */
const stun = (k: Kit): Clip<GB> =>
  gclip('stun', 2.4, true, (b, t) => {
    const p = phase(t, 2.4) * TAU;
    k.base(b, {
      ...k.ST,
      body: [0.25, 0, 0], chest: [0.3, 0, 0], head: [0.45, 0, 0], jaw: [0.3, 0, 0],
      armL: [-0.35, 0, 0.05], foreL: [-0.2, 0, 0], handL: [0.2, 0, 0], armR: [-0.4, 0, -0.05], foreR: [-0.2, 0, 0], handR: [0.2, 0, 0],
      legL: [-0.18, 0, -0.06], shinL: [0.35, 0, 0], legR: [-0.15, 0, 0.06], shinR: [0.32, 0, 0], root: [0, 0, 0, 0, -0.05, 0],
    });
    b.root.rot(0.03 * Math.sin(p), 0, 0.05 * Math.cos(p));
    b.chest.rot(0.05 * Math.sin(p * 2), 0.08 * Math.sin(p), -0.06 * Math.cos(p));
    b.head.rot(0.14 * Math.sin(p + 0.8), 0.32 * Math.cos(p + 0.8), 0.22 * Math.sin(p + 0.8));
    b.armL.rot(0.12 * Math.sin(p + 1.4), 0, -0.06 * Math.cos(p + 1));
    b.armR.rot(-0.12 * Math.sin(p + 1.4), 0, 0.06 * Math.cos(p + 1));
    b.shinR.rot(0.08 * Math.sin(p));
  });

/**
 * The fall (2.9 s): jolted upright, it staggers a step, its knees give (1.35 s, the ground shakes), it sways
 * there a moment with its head sunk, and pitches forward onto its face (2.45 s); the tower tumbles off.
 */
const die = (k: Kit): Clip<GB> =>
  gclip('die', 2.9, false, (b, t) => {
    const kneel: P = { legL: [0.0, 0, -0.1], shinL: [1.55, 0, 0], legR: [0.05, 0, 0.1], shinR: [1.55, 0, 0] };
    k.tl(b, t, [
      [0, {}],
      [0.25, { body: [-0.15, 0.1, 0.05], chest: [-0.25, 0.15, 0], head: [-0.55, 0.2, 0.1], jaw: [0.6, 0, 0], armL: [-1.5, 0, -0.7], foreL: [-0.3, 0, 0], armR: [-1.3, 0, 0.75], foreR: [-0.3, 0, 0], handL: [-0.2, 0, 0], handR: [-0.2, 0, 0], legL: [-0.1, 0, -0.04], shinL: [0.1, 0, 0], legR: [0.05, 0, 0.04], shinR: [0.1, 0, 0], root: [0, 0, 0, 0, 0.01, -0.07] }, outExpo],
      [0.85, { body: [0.35, -0.1, 0.08], chest: [0.3, -0.1, 0], head: [0.45, 0, 0.25], jaw: [0.4, 0, 0], armL: [-0.45, 0, -0.2], foreL: [-0.2, 0, 0], armR: [-0.4, 0, 0.2], foreR: [-0.2, 0, 0], handL: [0.3, 0, 0], handR: [0.3, 0, 0], legL: [0.25, 0, -0.04], shinL: [0.4, 0, 0], legR: [-0.35, 0, 0.04], shinR: [0.45, 0, 0], root: [0, 0, 0.05, 0, -0.1, 0.05] }, inOut],
      [1.35, { ...kneel, body: [0.25, 0, 0.05], chest: [0.22, 0, 0], head: [0.55, 0, 0.3], jaw: [0.35, 0, 0], armL: [-0.25, 0, -0.12], foreL: [-0.2, 0, 0], armR: [-0.25, 0, 0.12], foreR: [-0.2, 0, 0], handL: [0.3, 0, 0], handR: [0.3, 0, 0], root: [0, 0, 0, 0, -0.4, -0.04] }, inQuad],
      [1.5, { ...kneel, body: [0.3, 0, 0.05], chest: [0.26, 0, 0], head: [0.62, 0, 0.32], jaw: [0.35, 0, 0], armL: [-0.2, 0, -0.12], foreL: [-0.15, 0, 0], armR: [-0.2, 0, 0.12], foreR: [-0.15, 0, 0], handL: [0.3, 0, 0], handR: [0.3, 0, 0], root: [0, 0, 0, 0, -0.38, -0.04] }, outBack],
      [2.0, { ...kneel, body: [0.42, 0.1, 0.1], chest: [0.35, 0.05, 0.05], head: [0.85, 0.2, 0.45], jaw: [0.45, 0, 0], armL: [0.05, 0, -0.05], foreL: [-0.1, 0, 0], armR: [0.1, 0, 0.08], foreR: [-0.1, 0, 0], handL: [0.2, 0, 0], handR: [0.2, 0, 0], root: [0, 0, 0, 0, -0.4, -0.04] }, inOut],
      [2.45, { ...kneel, body: [1.3, 0.1, 0.12], chest: [0.32, 0.05, 0.05], head: [0.2, 0.5, 0.6], jaw: [0.5, 0, 0], armL: [-2.3, 0, -0.45], foreL: [-0.05, 0, 0], armR: [-2.2, 0, 0.5], foreR: [-0.05, 0, 0], handL: [-0.3, 0, 0], handR: [-0.3, 0, 0], root: [0, 0, 0, 0, -0.48, 0.04], tower: [0.3, 0, 1.3, -0.05, -0.2, 0] }, inQuad],
      [2.9, { ...kneel, body: [1.26, 0.1, 0.12], chest: [0.3, 0.05, 0.05], head: [0.3, 0.6, 0.7], jaw: [0.45, 0, 0], armL: [-2.32, 0, -0.47], foreL: [0.02, 0, 0], armR: [-2.22, 0, 0.52], foreR: [0.02, 0, 0], handL: [-0.35, 0, 0], handR: [-0.35, 0, 0], root: [0, 0, 0, 0, -0.48, 0.04], tower: [0.4, 0, 1.6, -0.08, -0.3, 0] }, outBack],
    ], { head: 0.06, foreL: 0.04, foreR: 0.04, tower: 0.06 });
    b.root.pos(0.012 * kick(t, 1.35, 7, 8) + 0.015 * kick(t, 2.45, 7, 8), 0, 0);
  }, (t) => thud(t, [[1.35, 0.05], [2.45, 0.07]]));

/* ---------- powers ---------- */

/** Lanzar roca: it stoops and tears a boulder from the ground behind its right foot, winds up overhead and hurls it (lets go at 0.6 s). */
const throwRock = (k: Kit): Clip<GB> =>
  gclip('throw', 1.7, false, (b, t) => {
    const reach: P = { body: [0.55, 0, 0.1], chest: [0.32, 0.45, 0.1], head: [0.2, 0.3, 0], armR: [0.45, 0, 0.35], foreR: [-0.3, 0, 0], handR: [0.4, 0, 0], armL: [-0.9, 0, -0.3], foreL: [-0.4, 0, 0], handL: [0.3, 0, 0], legL: [-0.35, 0, -0.06], shinL: [0.6, 0, 0], legR: [0.15, 0, 0.08], shinR: [0.6, 0, 0], root: [0, 0, 0, 0, -0.15, 0] };
    k.tl(b, t, [
      [0, {}],
      [0.26, reach, inOut],
      [0.34, { ...reach, body: [0.58, 0, 0.1], root: [0, 0, 0, 0, -0.16, 0] }, outQuad],
      [0.52, { body: [-0.22, 0, 0], chest: [-0.15, 0.55, 0], head: [-0.25, 0.2, 0], armR: [-2.75, 0, 0.35], foreR: [-1.4, 0, 0], handR: [0.45, 0, 0], armL: [-1.5, 0, -0.1], foreL: [-0.1, 0, 0], handL: [-0.1, 0, 0], legL: [-0.32, 0, -0.06], shinL: [0.3, 0, 0], legR: [0.15, 0, 0.08], shinR: [0.3, 0, 0], root: [0, 0, 0, 0, -0.03, -0.05] }, inOut],
      [0.6, { body: [0.38, 0, 0], chest: [0.3, -0.42, -0.05], head: [0.0, -0.2, 0], armR: [-1.45, 0, 0.12], foreR: [-0.1, 0, 0], handR: [-0.3, 0, 0], armL: [-0.3, 0, -0.45], foreL: [-0.3, 0, 0], handL: [0.2, 0, 0], legL: [-0.55, 0, -0.06], shinL: [0.5, 0, 0], legR: [0.3, 0, 0.08], shinR: [0.35, 0, 0], root: [0, 0, 0, 0, -0.1, 0.1] }, inCubic],
      [0.9, { body: [0.5, 0, 0], chest: [0.35, -0.55, -0.05], head: [0.1, -0.25, 0], armR: [-0.85, 0, -0.2], foreR: [-0.15, 0, 0], handR: [0.35, 0, 0], armL: [-0.25, 0, -0.45], foreL: [-0.3, 0, 0], handL: [0.2, 0, 0], legL: [-0.5, 0, -0.06], shinL: [0.5, 0, 0], legR: [0.3, 0, 0.08], shinR: [0.35, 0, 0], root: [0, 0, 0, 0, -0.1, 0.1] }, outCubic],
      [1.7, {}, inOut],
    ], { foreR: 0.02, handR: 0.03, head: 0.05 });
    // The boulder comes up out of the ground into its hand, and leaves it at the release.
    const held = span(t, 0.24, 0.34, outBack) * (t < 0.6 ? 1 : 0);
    b.rock.scale(Math.max(0.0001, held));
    b.root.pos(0.01 * kick(t, 0.6, 6, 8), 0, 0);
  }, (t) => thud(t, [[0.3, 0.02], [0.6, 0.03]]), 'Lanzar roca', true);

/** Pisotón: it rears onto one leg, arms flung out for balance, and brings the other down (0.5 s) with all its weight. */
const stomp = (k: Kit): Clip<GB> =>
  gclip('stomp', 1.5, false, (b, t) => {
    const slam: P = { body: [0.45, 0, 0], chest: [0.3, 0, 0], head: [0.25, 0, 0], armL: [-0.5, 0, -0.3], foreL: [-0.2, 0, 0], armR: [-0.5, 0, 0.3], foreR: [-0.2, 0, 0], legR: [-0.28, 0, 0.1], shinR: [0.22, 0, 0], legL: [-0.2, 0, -0.07], shinL: [0.45, 0, 0], root: [0, 0, 0, 0, -0.13, 0.05] };
    const lift: P = { body: [-0.12, 0, 0], chest: [-0.08, 0, 0], head: [-0.2, 0, 0], armL: [-0.85, 0, -0.75], foreL: [-0.45, 0, 0], armR: [-0.9, 0, 0.75], foreR: [-0.45, 0, 0], legR: [-1.15, 0, 0.12], shinR: [1.25, 0, 0], legL: [-0.08, 0, -0.05], shinL: [0.15, 0, 0], root: [0, 0, 0.06, 0, 0.05, -0.03] };
    k.tl(b, t, [
      [0, {}],
      [0.36, lift, inOut],
      [0.44, { ...lift, legR: [-1.25, 0, 0.12], shinR: [1.3, 0, 0], root: [0, 0, 0.065, 0, 0.065, -0.03] }, outQuad],
      [0.5, slam, inExpo],
      [0.8, { ...slam, body: [0.42, 0, 0], root: [0, 0, 0, 0, -0.12, 0.05] }, outBack],
      [1.5, {}, inOut],
    ], { head: 0.04, foreL: 0.03, foreR: 0.03 });
    b.root.pos(0.014 * kick(t, 0.5, 7, 7), 0, 0);
    b.jaw.rot(0.3 * envelope(t, 0.4, 0.5, 0.6, 0.9));
  }, (t) => thud(t, [[0.5, 0.09]]), 'Pisotón');

/**
 * Despertar: a jolt runs through it and its head snaps up; it looks round, gathers itself with its fists before
 * its chest, then flings its arms wide and roars (1.6 s), shaking, and settles into its waking stance.
 */
const wake = (k: Kit): Clip<GB> =>
  gclip('wake', 2.5, false, (b, t) => {
    const roar: P = { body: [-0.22, 0, 0], chest: [-0.25, 0, 0], head: [-0.55, 0, 0], jaw: [0.85, 0, 0], armL: [-0.75, 0, -1.25], foreL: [-0.4, 0, 0], handL: [-0.2, 0, 0], armR: [-0.75, 0, 1.25], foreR: [-0.4, 0, 0], handR: [-0.2, 0, 0], legL: [-0.15, 0, -0.1], shinL: [0.18, 0, 0], legR: [0.02, 0, 0.1], shinR: [0.12, 0, 0], root: [0, 0, 0, 0, 0.02, 0] };
    k.tl(b, t, [
      [0, S_ST],
      [0.28, { ...S_ST, head: [0.15, -0.22, 0.42], root: [0, 0, 0, 0, -0.01, 0] }],
      [0.38, { ...S_ST, body: [0.0, 0, 0], chest: [-0.06, 0, 0], head: [-0.45, 0, 0.05], jaw: [0.4, 0, 0], armL: [-1.0, 0, -0.25], foreL: [-0.3, 0, 0], handL: [0.1, 0, 0], armR: [-1.05, 0, 0.25], foreR: [-0.3, 0, 0], handR: [0.1, 0, 0], root: [0, 0, 0, 0, 0.03, -0.02] }, outExpo],
      [0.8, { ...A_ST, chest: [0.08, -0.18, 0], head: [-0.15, 0.4, 0], jaw: [0.2, 0, 0], armL: [-0.5, 0, -0.25], foreL: [-0.6, 0, 0], armR: [-0.5, 0, 0.25], foreR: [-0.6, 0, 0] }, inOut],
      [1.05, { ...A_ST, chest: [0.08, 0.15, 0], head: [-0.1, -0.35, 0], jaw: [0.2, 0, 0], armL: [-0.5, 0, -0.25], foreL: [-0.6, 0, 0], armR: [-0.5, 0, 0.25], foreR: [-0.6, 0, 0] }, inOut],
      [1.35, { ...A_ST, body: [0.3, 0, 0], chest: [0.3, 0, 0], head: [0.3, 0, 0], jaw: [0.05, 0, 0], armL: [-0.9, 0, 0.45], foreL: [-1.6, 0, 0], handL: [0.4, 0, 0], armR: [-0.9, 0, -0.45], foreR: [-1.6, 0, 0], handR: [0.4, 0, 0], legL: [-0.3, 0, -0.09], shinL: [0.55, 0, 0], legR: [-0.1, 0, 0.09], shinR: [0.45, 0, 0], root: [0, 0, 0, 0, -0.1, 0] }, inOut],
      [1.6, { ...A_ST, ...roar }, outBack],
      [2.1, { ...A_ST, ...roar, head: [-0.5, 0, 0], armL: [-0.72, 0, -1.2], armR: [-0.72, 0, 1.2] }],
      [2.5, A_ST, inOut],
    ], { head: 0.04, foreL: 0.03, foreR: 0.03, jaw: 0.03 });
    // The jolt, then the roar shaking it.
    const jolt = kick(t, 0.3, 5, 7);
    b.chest.rot(0.04 * jolt, 0, 0.03 * jolt);
    const tr = envelope(t, 1.55, 1.65, 2.05, 2.25) * shiver(t, 11);
    b.chest.rot(0.012 * tr, 0.015 * tr, 0.015 * tr);
    b.head.rot(0.02 * tr, 0.02 * tr, 0);
    b.jaw.rot(0.05 * tr);
  }, (t) => thud(t, [[0.36, 0.02], [1.6, 0.03]]), 'Despertar');

const clipsOf = (awake: boolean): Clip[] => {
  const k = kitOf(awake);
  return [
    awake ? idleAwake(k) : idleSleep(k),
    awake ? walkAwake(k) : walkSleep(k),
    attack(k), hit(k), stun(k), die(k), throwRock(k), stomp(k), wake(k),
  ] as Clip[];
};

export const giantClips = (): Clip[] => clipsOf(false);
export const giantAwakeClips = (): Clip[] => clipsOf(true);

/** Clips by library kind (see ../rigs.ts). */
export const CLIPS: Record<string, () => Clip[]> = { giant: giantClips, giantAwake: giantAwakeClips };
